import { assertNever } from "@redotech/util/type";

export enum ListToListMatchType {
  INCLUDES_ANY_OF = "INCLUDES_ANY_OF",
  INCLUDES_ALL_OF = "INCLUDES_ALL_OF",
  INCLUDES_ONLY = "INCLUDES_ONLY",
  HAS_NONE_OF = "HAS_NONE_OF",
  BEGINS_WITH = "ANY_BEGINS_WITH",
}

export enum StringToListMatchType {
  IS_ANY_OF = "IS_ANY_OF",
  IS_NONE_OF = "IS_NONE_OF",
  EXISTS = "EXISTS",
  CONTAINS_ANY_OF = "CONTAINS_ANY_OF",
  CONTAINS_NONE_OF = "CONTAINS_NONE_OF",
}

const stringToListMatchTypeToLabel: Record<StringToListMatchType, string> = {
  [StringToListMatchType.IS_ANY_OF]: "is any of",
  [StringToListMatchType.IS_NONE_OF]: "is none of",
  [StringToListMatchType.EXISTS]: "exists",
  [StringToListMatchType.CONTAINS_ANY_OF]: "contains any of",
  [StringToListMatchType.CONTAINS_NONE_OF]: "contains none of",
};

/**
 * Ex: Order tags include any of
 */
const listToListMatchTypeToLabel: Record<ListToListMatchType, string> = {
  [ListToListMatchType.INCLUDES_ANY_OF]: "include any of",
  [ListToListMatchType.INCLUDES_ALL_OF]: "include all of",
  [ListToListMatchType.INCLUDES_ONLY]: "include only",
  [ListToListMatchType.HAS_NONE_OF]: "have none of",
  [ListToListMatchType.BEGINS_WITH]: "any begins with",
};

/**
 * Ex: Order tag list includes any of
 */
const listToListMatchTypeToPluralLabel: Record<ListToListMatchType, string> = {
  [ListToListMatchType.INCLUDES_ANY_OF]: "includes any of",
  [ListToListMatchType.INCLUDES_ALL_OF]: "includes all of",
  [ListToListMatchType.INCLUDES_ONLY]: "includes only",
  [ListToListMatchType.HAS_NONE_OF]: "has none of",
  [ListToListMatchType.BEGINS_WITH]: "any begins with",
};

export const getMatchTypeLabel = (
  matchType: StringToListMatchType | ListToListMatchType,
  isPlural: boolean,
) => {
  if (matchType in StringToListMatchType) {
    return stringToListMatchTypeToLabel[matchType as StringToListMatchType];
  }
  if (isPlural) {
    return listToListMatchTypeToPluralLabel[matchType as ListToListMatchType];
  }
  return listToListMatchTypeToLabel[matchType as ListToListMatchType];
};

export function checkExactMatch({
  listFromGroup,
  listToMatch,
  matchType,
}: {
  listFromGroup: string[];
  listToMatch: string[];
  matchType: ListToListMatchType | undefined;
}): { passes: boolean; filteredFGroupList: string[] } {
  if (!matchType) {
    return { passes: false, filteredFGroupList: [] };
  }
  let passes: boolean;
  let itemsThatMatch: string[];
  switch (matchType) {
    case ListToListMatchType.INCLUDES_ANY_OF:
      itemsThatMatch = listFromGroup.filter((item) =>
        listToMatch.includes(item),
      );
      return {
        passes: itemsThatMatch.length > 0,
        filteredFGroupList: itemsThatMatch,
      };
    case ListToListMatchType.INCLUDES_ALL_OF:
      passes = listToMatch.every((item) => listFromGroup.includes(item));

      return { passes, filteredFGroupList: passes ? listFromGroup : [] };
    case ListToListMatchType.INCLUDES_ONLY:
      // Subset: every group value is in the match list (and the group is non-empty).
      passes =
        listFromGroup.length > 0 &&
        listFromGroup.every((item) => listToMatch.includes(item));
      return { passes, filteredFGroupList: passes ? listFromGroup : [] };
    case ListToListMatchType.HAS_NONE_OF:
      passes = listToMatch.every((item) => !listFromGroup.includes(item));
      return { passes, filteredFGroupList: passes ? listFromGroup : [] };
    case ListToListMatchType.BEGINS_WITH:
      itemsThatMatch = listFromGroup.filter((item) =>
        listToMatch.some((prefix) => item.startsWith(prefix)),
      );
      return {
        passes: itemsThatMatch.length > 0,
        filteredFGroupList: itemsThatMatch,
      };
    default:
      return assertNever(matchType);
  }
}

export function checkListContains({
  listFromGroup,
  listToMatch,
  matchType,
}: {
  listFromGroup: string[];
  listToMatch: string[];
  matchType: ListToListMatchType | undefined;
}): { passes: boolean; filteredFGroupList: string[] } {
  if (!matchType) {
    return { passes: false, filteredFGroupList: [] };
  }
  let passes: boolean;
  let itemsThatMatch: string[];
  switch (matchType) {
    case ListToListMatchType.INCLUDES_ANY_OF:
      itemsThatMatch = listFromGroup.filter((listFromGroupItem) =>
        listToMatch.some((listToMatchItem) =>
          listFromGroupItem.includes(listToMatchItem),
        ),
      );
      return {
        passes: itemsThatMatch.length > 0,
        filteredFGroupList: itemsThatMatch,
      };
    case ListToListMatchType.INCLUDES_ALL_OF:
      passes = listToMatch.every((listToMatchItem) =>
        listFromGroup.some((listFromGroupItem) =>
          listFromGroupItem.includes(listToMatchItem),
        ),
      );
      return { passes, filteredFGroupList: passes ? listFromGroup : [] }; // all or nothing
    case ListToListMatchType.INCLUDES_ONLY:
      // Subset: every group value is covered by some match substring (and the group is non-empty).
      passes =
        listFromGroup.length > 0 &&
        listFromGroup.every((listFromGroupItem) =>
          listToMatch.some((listToMatchItem) =>
            listFromGroupItem.includes(listToMatchItem),
          ),
        );
      return { passes, filteredFGroupList: passes ? listFromGroup : [] }; // all or nothing
    case ListToListMatchType.HAS_NONE_OF:
      passes = listToMatch.every((listToMatchItem) =>
        listFromGroup.some(
          (listFromGroupItem) => !listFromGroupItem.includes(listToMatchItem),
        ),
      );
      return { passes, filteredFGroupList: passes ? listFromGroup : [] }; // all or nothing
    case ListToListMatchType.BEGINS_WITH:
      itemsThatMatch = listFromGroup.filter((item) =>
        listToMatch.some((prefix) => item.startsWith(prefix)),
      );
      return {
        passes: itemsThatMatch.length > 0,
        filteredFGroupList: itemsThatMatch,
      };
    default:
      return assertNever(matchType);
  }
}
